// Shared pieces for the Leah-facing pages: one job per page, large type,
// plain words, every destructive action confirms in one sentence.
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isNotConfigured, errorText } from '../lib/backbarApi';

/** Page header with a visible Back (chevron + the word) on every page but Home. */
export function PageHead({
  title, sub, back = '/', backLabel = 'Back', right,
}: {
  title: string;
  sub?: ReactNode;
  back?: string | null;
  backLabel?: string;
  right?: ReactNode;
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const goBack = () => {
    // History when there is some; the fallback keeps Back from reading as frozen.
    if (window.history.length > 1 && loc.key !== 'default') nav(-1);
    else nav(back ?? '/');
  };
  return (
    <div className="lp-head">
      {back !== null && (
        <button type="button" className="lp-back" onClick={goBack}>
          <span aria-hidden="true">‹</span> {backLabel}
        </button>
      )}
      <div className="lp-head-row">
        <div>
          <h1 className="lp-title">{title}</h1>
          {sub && <p className="lp-sub">{sub}</p>}
        </div>
        {right && <div className="lp-head-right">{right}</div>}
      </div>
    </div>
  );
}

/** A calm note. `tone` picks the wash; none of them shout. */
export function Note({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'good' | 'warn' }) {
  return <div className={`lp-note lp-note--${tone}`}>{children}</div>;
}

/** Turns any thrown value into a note; not_configured reads as "Not connected yet". */
export function ErrorNote({ error, notConfigured }: { error: unknown; notConfigured?: ReactNode }) {
  if (!error) return null;
  if (isNotConfigured(error)) {
    return <Note>{notConfigured ?? 'Not connected yet.'}</Note>;
  }
  return <Note tone="warn">{errorText(error)}</Note>;
}

/** Label + control, stacked. */
export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="lp-field">
      <span className="lp-field-label">{label}</span>
      {children}
      {hint && <span className="lp-field-hint">{hint}</span>}
    </label>
  );
}

/**
 * A button that asks one sentence before it acts.
 * Click → the sentence appears with "Yes, <verb>" and "No, leave it".
 */
export function ConfirmButton({
  label, sentence, yes, onConfirm, tone = 'normal', disabled, busyLabel,
}: {
  label: string;
  sentence: string;
  yes?: string;
  onConfirm: () => Promise<void> | void;
  tone?: 'normal' | 'primary' | 'danger';
  disabled?: boolean;
  busyLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cls = tone === 'primary' ? 'lp-btn lp-btn--primary' : tone === 'danger' ? 'lp-btn lp-btn--danger' : 'lp-btn';
  if (!open) {
    return (
      <button type="button" className={cls} disabled={disabled || busy} onClick={() => setOpen(true)}>
        {busy ? (busyLabel ?? 'Working…') : label}
      </button>
    );
  }
  return (
    <div className="lp-confirm">
      <p className="lp-confirm-text">{sentence}</p>
      <div className="lp-row">
        <button
          type="button"
          className={cls}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
              setOpen(false);
            }
          }}
        >
          {busy ? (busyLabel ?? 'Working…') : (yes ?? `Yes, ${label.toLowerCase()}`)}
        </button>
        <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={() => setOpen(false)}>
          No, leave it
        </button>
      </div>
    </div>
  );
}

/** Large on/off switch, labeled with words. */
export function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={on ? 'lp-switch on' : 'lp-switch'}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      <span className="lp-switch-knob" />
      <span className="lp-switch-word">{on ? 'On' : 'Off'}</span>
    </button>
  );
}

/** A card that links somewhere, with a big number and a plain line. */
export function LinkCard({ to, count, title, line }: { to: string; count: number | null; title: string; line: ReactNode }) {
  return (
    <Link to={to} className={count ? 'lp-card lp-card--link lp-card--live' : 'lp-card lp-card--link'}>
      <div className="lp-card-count">{count == null ? '—' : count}</div>
      <div className="lp-card-title">{title}</div>
      <div className="lp-card-line">{line}</div>
    </Link>
  );
}

export function Section({ title, children, right }: { title: string; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="lp-section">
      <div className="lp-section-head">
        <h2 className="lp-section-title">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="lp-empty">{children}</p>;
}
